import "./i18n";
import { loadLanguage } from "./i18n";
import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Index from "./pages/Index";
import { useGA4PageView } from "./hooks/useGA4PageView";

const Devis = lazy(() => import("./pages/Devis"));
const LeadNudge = lazy(() => import("./components/LeadNudge"));
const CalendlyPopup = lazy(() => import("./components/CalendlyPopup"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BlogRefonteSite = lazy(() => import("./pages/blog/BlogRefonteSite"));
const BlogCoreWebVitals = lazy(() => import("./pages/blog/BlogCoreWebVitals"));
const BlogSiteCabinetComptable = lazy(() => import("./pages/blog/BlogSiteCabinetComptable"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogConversion = lazy(() => import("./pages/blog/BlogConversion"));
const BlogWordpressVsShopify = lazy(() => import("./pages/blog/BlogWordpressVsShopify"));
const BlogWixVsWordpress = lazy(() => import("./pages/blog/BlogWixVsWordpress"));
const BlogWebflowVsWordpress = lazy(() => import("./pages/blog/BlogWebflowVsWordpress"));
const BlogShopifyVsWoocommerce = lazy(() => import("./pages/blog/BlogShopifyVsWoocommerce"));
const BlogSiteOnePageVsMultiPages = lazy(() => import("./pages/blog/BlogSiteOnePageVsMultiPages"));
const BlogCreerEntrepriseAndorre = lazy(() => import("./pages/blog/BlogCreerEntrepriseAndorre"));
const BlogPrixSiteInternetToulouse = lazy(() => import("./pages/blog/BlogPrixSiteInternetToulouse"));
const BlogGoogleAdsOuSeo = lazy(() => import("./pages/blog/BlogGoogleAdsOuSeo"));
const BlogAgenceWebVsFreelance = lazy(() => import("./pages/blog/BlogAgenceWebVsFreelance"));
const BlogChoisirAgenceWeb = lazy(() => import("./pages/blog/BlogChoisirAgenceWeb"));
const BlogChoisirAgenceWebAndorre = lazy(() => import("./pages/blog/BlogChoisirAgenceWebAndorre"));
const BlogTriarAgenciaWebAndorra = lazy(() => import("./pages/blog/BlogTriarAgenciaWebAndorra"));
const BlogCrearPaginaWebAndorra = lazy(() => import("./pages/blog/BlogCrearPaginaWebAndorra"));
const BlogSeoLocal = lazy(() => import("./pages/blog/BlogSeoLocal"));
const BlogCahierDesChargesSiteWeb = lazy(() => import("./pages/blog/BlogCahierDesChargesSiteWeb"));
const BlogGoogleAnalytics4 = lazy(() => import("./pages/blog/BlogGoogleAnalytics4"));
const BlogRefonteSiteWebPrix = lazy(() => import("./pages/blog/BlogRefonteSiteWebPrix"));
const BlogPrixSiteEcommerce = lazy(() => import("./pages/blog/BlogPrixSiteEcommerce"));
const BlogPrixSiteVitrine = lazy(() => import("./pages/blog/BlogPrixSiteVitrine"));
const BlogAuditSeoPme = lazy(() => import("./pages/blog/BlogAuditSeoPme"));
const BlogCreerSiteWebPro = lazy(() => import("./pages/blog/BlogCreerSiteWebPro"));
const AgenceWebAndorre = lazy(() => import("./pages/AgenceWebAndorre"));
const AgenciaWebAndorraES = lazy(() => import("./pages/AgenciaWebAndorraES"));
const AgenciaWebAndorraCA = lazy(() => import("./pages/AgenciaWebAndorraCA"));
const AgenceWebToulouse = lazy(() => import("./pages/AgenceWebToulouse"));
const AgenceWebBordeaux = lazy(() => import("./pages/AgenceWebBordeaux"));
const AgenceWebLyon = lazy(() => import("./pages/AgenceWebLyon"));
const ServiceSeo = lazy(() => import("./pages/services/Seo"));
const ServiceCreationSite = lazy(() => import("./pages/services/CreationSite"));
const CaseStudy = lazy(() => import("./pages/realisations/CaseStudy"));
const ServiceGoogleAds = lazy(() => import("./pages/services/GoogleAds"));

const queryClient = new QueryClient();

const useLangFromUrl = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    let target: string | null = null;
    if (pathname === "/en" || pathname === "/en/") target = "en";
    else if (pathname === "/ca" || pathname === "/ca/") target = "ca";
    else if (pathname === "/" || pathname === "/fr" || pathname === "/fr/") target = "fr";
    if (!target) return;
    loadLanguage(target).then(() => i18n.changeLanguage(target!));
  }, [pathname, i18n]);
};

// Carte visiteur + popup Calendly : chargées après la page pour ne pas freiner l'affichage.
const useAfterLoad = (delay = 3000) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let timer = 0;
    const start = () => { timer = window.setTimeout(() => setReady(true), delay); };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => { window.clearTimeout(timer); window.removeEventListener("load", start); };
  }, [delay]);
  return ready;
};

const AppRoutes = () => {
  useGA4PageView();
  useLangFromUrl();
  const extrasReady = useAfterLoad();
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#f4f3ed]" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/en/" element={<Index />} />
        <Route path="/ca/" element={<Index />} />
        <Route path="/devis" element={<Devis />} />
        <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/conversion" element={<BlogConversion />} />
        <Route path="/blog/wordpress-vs-shopify" element={<BlogWordpressVsShopify />} />
        <Route path="/blog/wix-vs-wordpress" element={<BlogWixVsWordpress />} />
        <Route path="/blog/webflow-vs-wordpress" element={<BlogWebflowVsWordpress />} />
        <Route path="/blog/shopify-vs-woocommerce" element={<BlogShopifyVsWoocommerce />} />
        <Route path="/blog/site-one-page-vs-site-multi-pages" element={<BlogSiteOnePageVsMultiPages />} />
        <Route path="/blog/creer-entreprise-andorre-guide-digital" element={<BlogCreerEntrepriseAndorre />} />
        <Route path="/blog/prix-site-internet-toulouse" element={<BlogPrixSiteInternetToulouse />} />
        <Route path="/blog/google-ads-ou-seo-ou-investir" element={<BlogGoogleAdsOuSeo />} />
        <Route path="/blog/agence-web-vs-freelance" element={<BlogAgenceWebVsFreelance />} />
        <Route path="/blog/comment-choisir-agence-web" element={<BlogChoisirAgenceWeb />} />
        <Route path="/blog/choisir-agence-web-andorre" element={<BlogChoisirAgenceWebAndorre />} />
        <Route path="/ca/blog/triar-agencia-web-andorra" element={<BlogTriarAgenciaWebAndorra />} />
        <Route path="/es/blog/crear-pagina-web-andorra" element={<BlogCrearPaginaWebAndorra />} />
        <Route path="/blog/seo-local-google-2026" element={<BlogSeoLocal />} />
        {/* Removed posts redirected to /blog */}
        <Route path="/blog/meilleures-agences-web-andorre" element={<Navigate to="/blog" replace />} />
        <Route path="/blog/meilleures-agences-web-toulouse" element={<Navigate to="/blog" replace />} />
        <Route path="/agence-web-andorre" element={<AgenceWebAndorre />} />
        <Route path="/es/agencia-web-andorra" element={<AgenciaWebAndorraES />} />
        <Route path="/ca/agencia-web-andorra" element={<AgenciaWebAndorraCA />} />
        <Route path="/agence-web-toulouse" element={<AgenceWebToulouse />} />
        <Route path="/blog/refonte-site-web-7-signes" element={<BlogRefonteSite />} />
        <Route path="/blog/core-web-vitals-expliques" element={<BlogCoreWebVitals />} />
        <Route path="/blog/site-internet-cabinet-comptable-guide-2026" element={<BlogSiteCabinetComptable />} />
        <Route path="/blog/comment-choisir-agence-web-lille" element={<Navigate to="/blog" replace />} />
        <Route path="/blog/cahier-des-charges-site-web-2026" element={<BlogCahierDesChargesSiteWeb />} />
        <Route path="/blog/google-analytics-4-pme-guide-2026" element={<BlogGoogleAnalytics4 />} />
        <Route path="/blog/refonte-site-web-prix-2026" element={<BlogRefonteSiteWebPrix />} />
        <Route path="/blog/prix-site-ecommerce-2026" element={<BlogPrixSiteEcommerce />} />
        <Route path="/blog/prix-site-vitrine-2026" element={<BlogPrixSiteVitrine />} />
        <Route path="/blog/audit-seo-pme" element={<BlogAuditSeoPme />} />
        <Route path="/blog/creer-site-web-professionnel-erreurs" element={<BlogCreerSiteWebPro />} />
        <Route path="/services/seo" element={<ServiceSeo />} />
        <Route path="/services/creation-site" element={<ServiceCreationSite />} />
        <Route path="/realisations/:slug" element={<CaseStudy />} />
        <Route path="/services/google-ads" element={<ServiceGoogleAds />} />
        <Route path="/agence-web-bordeaux" element={<AgenceWebBordeaux />} />
        <Route path="/agence-web-lyon" element={<AgenceWebLyon />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Suspense fallback={null}>
        {extrasReady && <LeadNudge />}
        {extrasReady && <CalendlyPopup />}
        {extrasReady && <WhatsAppButton />}
      </Suspense>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
